import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { supabase } from '../lib/supabase';
import { EyeSlashIcon, MagnifyingGlassIcon, TrophyIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import PhotoLightbox from '../components/PhotoLightbox';
import type { Photo, PhotoCategory } from '../types/content';

interface Tournament {
  id: string;
  name: string;
  start_date: string;
  year?: number;
}

interface GalleryItem {
  id: string;
  title: string;
  tournament_id: string | null;
  categories: PhotoCategory[];
}

export default function Gallery() {
  const [tournaments, setTournaments] = useState<Tournament[]>([]);
  const [selectedTournamentId, setSelectedTournamentId] = useState<string>('all');
  const [galleryItems, setGalleryItems] = useState<GalleryItem[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  const [selectedPhotoIndex, setSelectedPhotoIndex] = useState(0);
  const [isPublished, setIsPublished] = useState(true);
  const [filteredPhotos, setFilteredPhotos] = useState<Photo[]>([]);
  const [tournamentDropdownOpen, setTournamentDropdownOpen] = useState(false);

  useEffect(() => {
    fetchTournaments();
    fetchGalleryData();
    checkGalleryPublishStatus();
  }, []);

  useEffect(() => {
    setSelectedCategory('all');
    setSearchTerm('');
  }, [selectedTournamentId]);

  useEffect(() => {
    filterPhotos();
  }, [galleryItems, selectedTournamentId, selectedCategory, searchTerm]);

  const checkGalleryPublishStatus = async () => {
    try {
      const { data, error } = await supabase
        .from('system_settings')
        .select('value')
        .eq('key', 'gallery_published')
        .maybeSingle();

      if (error && !error.message.includes('No rows found')) {
        console.error('Error checking gallery publish status:', error);
        return;
      }

      setIsPublished(data?.value === true || data?.value === 'true');
    } catch (err) {
      console.error('Error checking gallery publish status:', err);
    }
  };

  const fetchTournaments = async () => {
    try {
      const { data } = await supabase
        .from('tournaments')
        .select('id, name, start_date')
        .order('start_date', { ascending: false });

      if (data) {
        setTournaments(data.map(t => ({
          ...t,
          year: new Date(t.start_date).getFullYear()
        })));
      }
    } catch (err) {
      console.error('Error fetching tournaments:', err);
    }
  };

  const fetchGalleryData = async () => {
    try {
      setLoading(true);

      const { data: contentSections, error: contentError } = await supabase
        .from('content_sections')
        .select(`
          id,
          content_items (*)
        `)
        .eq('page', 'history')
        .eq('title', 'Gallery')
        .single();

      if (contentError && !contentError.message.includes('No rows found')) {
        throw contentError;
      }

      if (contentSections?.content_items && contentSections.content_items.length > 0) {
        const publishedItems = contentSections.content_items.filter(
          (item: any) => item.type === 'gallery' && item.status === 'Published'
        );

        const processed: GalleryItem[] = [];

        for (const item of publishedItems) {
          try {
            let parsedContent = item.content;
            let parseAttempts = 0;

            while (typeof parsedContent === 'string' && parseAttempts < 5) {
              try {
                parsedContent = JSON.parse(parsedContent);
                parseAttempts++;
              } catch {
                break;
              }
            }

            if (Array.isArray(parsedContent)) {
              processed.push({
                id: item.id,
                title: item.title,
                tournament_id: item.tournament_id || null,
                categories: parsedContent as PhotoCategory[],
              });
            }
          } catch (e) {
            console.error('Error parsing gallery content:', e);
          }
        }

        setGalleryItems(processed);
      }
    } catch (err) {
      console.error('Error fetching gallery data:', err);
    } finally {
      setLoading(false);
    }
  };

  const getActiveCategories = (): PhotoCategory[] => {
    const items = selectedTournamentId === 'all'
      ? galleryItems
      : galleryItems.filter(g => g.tournament_id === selectedTournamentId);

    const allCategories: PhotoCategory[] = [];
    items.forEach(item => allCategories.push(...item.categories));
    return allCategories;
  };

  const filterPhotos = () => {
    const categories = getActiveCategories();
    let allPhotos: Photo[] = [];

    if (selectedCategory !== 'all') {
      const category = categories.find(c => c.name === selectedCategory);
      allPhotos = category?.photos?.filter(p => p && p.url) || [];
    } else {
      categories.forEach(category => {
        if (category.photos && category.photos.length > 0) {
          const validPhotos = category.photos.filter(p => p && p.url);
          allPhotos = [...allPhotos, ...validPhotos];
        }
      });
    }

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      allPhotos = allPhotos.filter(photo =>
        photo.caption && photo.caption.toLowerCase().includes(term)
      );
    }

    setFilteredPhotos(allPhotos);
  };

  const openLightbox = (index: number) => {
    setSelectedPhotoIndex(index);
    setIsLightboxOpen(true);
  };

  const activeCategories = getActiveCategories();
  const selectedTournament = tournaments.find(t => t.id === selectedTournamentId);

  return (
    <div className="pt-20">
      {!isPublished && (
        <div className="fixed top-24 inset-x-0 z-50 bg-yellow-100 border-b border-yellow-200 py-2">
          <div className="container mx-auto px-4">
            <div className="flex items-center justify-center gap-2 text-yellow-800">
              <EyeSlashIcon className="w-5 h-5" />
              <p className="text-sm font-medium">
                This page is currently unpublished and only visible to administrators
              </p>
            </div>
          </div>
        </div>
      )}

      <section className="relative bg-golf-gray text-white py-24">
        <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1587174486073-ae5e5cff23aa')] bg-cover bg-center opacity-20"></div>
        <div className="container mx-auto px-4 relative">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl mx-auto text-center"
          >
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-display font-bold mb-6">
              Photo Gallery
            </h1>
            <p className="text-xl text-golf-sand mb-10">
              Capturing the moments that make Ibom Open special
            </p>

            <div className="flex flex-wrap justify-center gap-3">
              <button
                onClick={() => setSelectedTournamentId('all')}
                className={`px-5 py-2.5 rounded-full text-sm font-medium transition-all duration-200 ${
                  selectedTournamentId === 'all'
                    ? 'bg-golf-orange text-white shadow-lg scale-105'
                    : 'bg-white/10 text-white hover:bg-white/20 backdrop-blur-sm'
                }`}
              >
                All Tournaments
              </button>
              {tournaments.slice(0, 5).map(t => (
                <button
                  key={t.id}
                  onClick={() => setSelectedTournamentId(t.id)}
                  className={`px-5 py-2.5 rounded-full text-sm font-medium transition-all duration-200 ${
                    selectedTournamentId === t.id
                      ? 'bg-golf-orange text-white shadow-lg scale-105'
                      : 'bg-white/10 text-white hover:bg-white/20 backdrop-blur-sm'
                  }`}
                >
                  {t.year || t.name}
                </button>
              ))}
              {tournaments.length > 5 && (
                <div className="relative">
                  <button
                    onClick={() => setTournamentDropdownOpen(!tournamentDropdownOpen)}
                    className={`flex items-center gap-1 px-5 py-2.5 rounded-full text-sm font-medium transition-all duration-200 ${
                      !['all', ...tournaments.slice(0, 5).map(t => t.id)].includes(selectedTournamentId)
                        ? 'bg-golf-orange text-white shadow-lg scale-105'
                        : 'bg-white/10 text-white hover:bg-white/20 backdrop-blur-sm'
                    }`}
                  >
                    {!['all', ...tournaments.slice(0, 5).map(t => t.id)].includes(selectedTournamentId)
                      ? (selectedTournament?.year || selectedTournament?.name || 'More')
                      : 'More'}
                    <ChevronDownIcon className={`w-4 h-4 transition-transform ${tournamentDropdownOpen ? 'rotate-180' : ''}`} />
                  </button>
                  <AnimatePresence>
                    {tournamentDropdownOpen && (
                      <motion.div
                        initial={{ opacity: 0, y: -8 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -8 }}
                        className="absolute top-full mt-2 right-0 bg-white rounded-xl shadow-xl border border-gray-100 py-2 min-w-[180px] z-20"
                      >
                        {tournaments.slice(5).map(t => (
                          <button
                            key={t.id}
                            onClick={() => {
                              setSelectedTournamentId(t.id);
                              setTournamentDropdownOpen(false);
                            }}
                            className={`w-full text-left px-4 py-2.5 text-sm transition-colors ${
                              selectedTournamentId === t.id
                                ? 'bg-golf-orange/10 text-golf-orange font-medium'
                                : 'text-gray-700 hover:bg-gray-50'
                            }`}
                          >
                            {t.name}
                          </button>
                        ))}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              )}
            </div>
          </motion.div>
        </div>
      </section>

      {selectedTournamentId !== 'all' && selectedTournament && (
        <div className="bg-golf-orange/5 border-b border-golf-orange/20">
          <div className="container mx-auto px-4 py-4">
            <div className="flex items-center gap-2 text-golf-orange">
              <TrophyIcon className="w-5 h-5 flex-shrink-0" />
              <span className="font-medium">{selectedTournament.name}</span>
              <span className="text-gray-400 text-sm ml-auto">
                {filteredPhotos.length} photo{filteredPhotos.length !== 1 ? 's' : ''}
              </span>
            </div>
          </div>
        </div>
      )}

      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-7xl mx-auto">
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-8">
              <div className="flex flex-col md:flex-row gap-4">
                <div className="flex-1 relative">
                  <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search by caption..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-golf-orange focus:border-golf-orange"
                  />
                </div>

                <select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-golf-orange focus:border-golf-orange"
                >
                  <option value="all">All Categories</option>
                  {activeCategories.map((category, index) => (
                    <option key={index} value={category.name}>{category.name}</option>
                  ))}
                </select>
              </div>
            </div>

            {loading ? (
              <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-4 border-golf-orange border-t-transparent"></div>
              </div>
            ) : (
              <>
                {selectedCategory === 'all' ? (
                  activeCategories.map((category, categoryIndex) => (
                    <div key={categoryIndex} className="mb-12 last:mb-0">
                      <motion.h2
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.5 }}
                        className="text-2xl md:text-3xl font-display font-bold text-golf-gray mb-6"
                      >
                        {category.name}
                      </motion.h2>

                      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                        {category.photos?.filter(p => p && p.url).map((photo, photoIndex) => {
                          const globalIndex = activeCategories
                            .slice(0, categoryIndex)
                            .reduce((acc, cat) => acc + (cat.photos?.filter(p => p && p.url).length || 0), 0) + photoIndex;

                          return (
                            <motion.div
                              key={photoIndex}
                              initial={{ opacity: 0, scale: 0.9 }}
                              whileInView={{ opacity: 1, scale: 1 }}
                              viewport={{ once: true }}
                              transition={{ duration: 0.3, delay: photoIndex * 0.05 }}
                              className="group cursor-pointer"
                              onClick={() => openLightbox(globalIndex)}
                            >
                              <div className="aspect-square rounded-lg overflow-hidden shadow-md hover:shadow-xl transition-all duration-300">
                                <img
                                  src={photo.url}
                                  alt={photo.caption || `Gallery image ${photoIndex + 1}`}
                                  className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-300"
                                  loading="lazy"
                                />
                              </div>
                              {photo.caption && (
                                <p className="mt-2 text-sm text-gray-600 line-clamp-2">{photo.caption}</p>
                              )}
                            </motion.div>
                          );
                        })}
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                    {filteredPhotos.map((photo, photoIndex) => (
                      <motion.div
                        key={photoIndex}
                        initial={{ opacity: 0, scale: 0.9 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.3, delay: photoIndex * 0.05 }}
                        className="group cursor-pointer"
                        onClick={() => openLightbox(photoIndex)}
                      >
                        <div className="aspect-square rounded-lg overflow-hidden shadow-md hover:shadow-xl transition-all duration-300">
                          <img
                            src={photo.url}
                            alt={photo.caption || `Gallery image ${photoIndex + 1}`}
                            className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-300"
                            loading="lazy"
                          />
                        </div>
                        {photo.caption && (
                          <p className="mt-2 text-sm text-gray-600 line-clamp-2">{photo.caption}</p>
                        )}
                      </motion.div>
                    ))}
                  </div>
                )}

                {filteredPhotos.length === 0 && !loading && (
                  <div className="text-center py-20">
                    <TrophyIcon className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500 font-medium">
                      {selectedTournamentId !== 'all'
                        ? 'No photos available for this tournament yet.'
                        : 'No photos found.'}
                    </p>
                  </div>
                )}

                <PhotoLightbox
                  isOpen={isLightboxOpen}
                  onClose={() => setIsLightboxOpen(false)}
                  photos={selectedCategory === 'all'
                    ? activeCategories.flatMap(c => c.photos?.filter(p => p && p.url) || [])
                    : filteredPhotos
                  }
                  initialIndex={selectedPhotoIndex}
                />
              </>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
